import React, { useEffect } from 'react';

// Écran « séance terminée » : la vraie FIN que le flux infini n'avait pas. Le héros
// est désormais la PERFORMANCE de la séance — l'anneau se remplit selon la justesse
// (bonnes réponses / répondues) et se colore vert si c'était bon, ambre sinon : on
// distingue enfin une bonne d'une mauvaise session d'un coup d'œil. « Sauvés de
// l'oubli » (aversion à la perte) reste, en secondaire. Zéro blabla.

export default function SessionComplete({
  rescued, done, correct, total,
  horizon, lang, onContinue, invite, onOpenAccount,
}) {
  const t = lang.ui.session;
  const s = lang.ui.srs;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' || e.key === 'Enter') onContinue(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onContinue]);

  // Justesse de la séance → remplissage + couleur de l'anneau.
  const answered = total || 0;
  const good = correct || 0;
  const acc = answered ? good / answered : 0;
  const ok = acc >= 0.7;
  const ringColor = ok ? '#34d399' : '#fbbf24';
  const R = 76;
  const C = 2 * Math.PI * R; // ≈ 477.5

  const due = horizon?.due ?? 0;
  const solid = horizon?.solid ?? 0;
  // « bientôt » (soon) retiré du bilan à la demande : on ne garde que « à revoir »
  // (échus) et « solides ».
  const hasHorizon = due + solid > 0;
  const seg = (n, cls) => (n ? <div style={{ flexGrow: n }} className={cls} /> : null);

  return (
    /* Même tuile que la carte de quiz : même largeur (celle du conteneur), même
       fond, même filet, même arrondi. Elle en était une autre (max-w-sm, filet plus
       dur), et le changement de format à chaque fin de série se voyait. */
    <div className="bg-slate-800 border border-slate-700/50 rounded-2xl px-5 md:px-7 py-8 w-full flex flex-col items-center gap-5 shadow-xl animate-fade-in-up motion-reduce:animate-none">
        <div className="flex items-center justify-between w-full">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{t.doneTitle}</span>
        </div>

        {/* Anneau de justesse : rempli à la proportion de bonnes réponses. */}
        <div className="relative w-44 h-44">
          <svg viewBox="0 0 176 176" className="w-44 h-44 -rotate-90">
            <circle cx="88" cy="88" r="76" fill="none" stroke="#1e293b" strokeWidth="12" />
            {answered > 0 && (
              <circle
                cx="88" cy="88" r="76" fill="none" stroke={ringColor} strokeWidth="12"
                strokeLinecap="round" strokeDasharray={`${acc * C} ${C}`}
                style={{ transition: 'stroke-dasharray 700ms ease-out' }}
              />
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
            <div className="flex items-baseline gap-0.5">
              <span className="text-5xl font-extrabold tabular-nums leading-none" style={{ color: ringColor }}>{good}</span>
              <span className="text-xl font-semibold text-slate-500 tabular-nums">/{answered}</span>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t.correct}</span>
          </div>
        </div>

        {/* Aversion à la perte : mots échus rattrapés (secondaire). */}
        {rescued > 0 && (
          <p className="text-[13px] font-semibold text-emerald-300 -mt-1">
            {rescued} {t.rescued}
          </p>
        )}

        {hasHorizon && (
          <div className="w-full flex flex-col gap-2">
            <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-900">
              {seg(due, 'bg-amber-400/80')}
              {seg(solid, 'bg-emerald-400/70')}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
              {due > 0 && <span><span className="font-bold text-amber-300">{due}</span> {s.due}</span>}
              {solid > 0 && <span><span className="font-bold text-emerald-300">{solid}</span> {s.solid}</span>}
            </div>
          </div>
        )}

        <button
          onClick={onContinue}
          className="mt-1 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-semibold text-white transition-colors"
        >
          {t.continueBtn}
        </button>

        {/* Le compte n'est PAS une inscription, c'est la portabilité de ce qu'on
            vient de gagner. On le propose à chaque bilan tant qu'il n'y a pas de
            compte : une ligne de 11 px sous le bouton, assez discrète pour ne pas
            harceler. Jamais sur la surface du quiz. */}
        {invite && onOpenAccount && (
          <p className="text-[11px] text-slate-500 text-center leading-snug -mt-1">
            {lang.ui.account.inviteLine}{' '}
            <button
              onClick={onOpenAccount}
              className="text-slate-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
            >
              {lang.ui.account.inviteCta}
            </button>
          </p>
        )}
    </div>
  );
}
